import { Box, Flex } from '@rebass/grid';
import {
  formatHumanReadableNumber,
  formatMoney,
  formatPercentage,
} from 'helpers/formatters/number';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrophy } from 'react-icons/fa';
import { MdShowChart } from 'react-icons/md';
import { Coin } from 'store/Coin/types';
import { Currency } from 'store/Currency/types';
import withCurrency from 'store/Currency/with-currency';
import { Market } from 'store/Market/types';

import withContainer from './container';
import {
  ChartIconWrapper,
  Container,
  Label,
  PercentageValue,
  Rank,
  TrophyIcon,
  Value,
} from './styles';

interface Props {
  market: Market;
  coin: Coin;
  currency: Currency;
}

interface RankingProps {
  rank: number | null;
}

const RankBadge = (props: RankingProps) => {
  const { t } = useTranslation();
  const { rank } = props;

  if (rank === null) {
    return null;
  }

  return (
    <Rank>
      <TrophyIcon>
        <FaTrophy />
      </TrophyIcon>
      {t('label.rank')} {rank}
    </Rank>
  );
};

const MarketData = (props: Props) => {
  const { t } = useTranslation();
  const { currency, market, coin } = props;

  return (
    <Container flexDirection="column">
      <h2>{t('label.market-data')}</h2>

      {market.marketCapRank && <RankBadge rank={market.marketCapRank} />}

      <Flex flex={1} flexDirection="column" justifyContent="space-between">
        <Box width={1} pt="16px">
          <Label>{t('label.market-cap')}</Label>
          <Value>
            <span>
              {market.marketCap
                ? formatHumanReadableNumber(market.marketCap, currency)
                : '--'}
            </span>
            {market.marketCapPercentChange24h && (
              <PercentageValue percent={market.marketCapPercentChange24h || 0}>
                <ChartIconWrapper>
                  <MdShowChart />
                </ChartIconWrapper>
                <span>
                  {formatPercentage(market.marketCapPercentChange24h)}
                </span>
              </PercentageValue>
            )}
          </Value>
        </Box>

        <Flex width={1} py="8px">
          <Box width={1 / 2}>
            <Label>{t('label.low-24h')}</Label>
            <Value>
              {market.low24h ? formatMoney(market.low24h, currency) : '--'}
            </Value>
          </Box>
          <Box width={1 / 2}>
            <Label>{t('label.high-24h')}</Label>
            <Value>
              {market.high24h ? formatMoney(market.high24h, currency) : '--'}
            </Value>
          </Box>
        </Flex>

        <Flex width={1} pt="8px">
          <Box width={1 / 2}>
            <Label>{t('label.volume')}</Label>
            <Value>
              {market.volume24h
                ? formatHumanReadableNumber(market.volume24h, currency)
                : '--'}
            </Value>
          </Box>
          <Box width={1 / 2}>
            <Label>{t('label.circulating-supply')}</Label>
            <Value>
              {market.availableSupply
                ? `${formatHumanReadableNumber(market.availableSupply)} ${
                    coin.code
                  }`
                : '--'}
            </Value>
          </Box>
        </Flex>
      </Flex>
    </Container>
  );
};

export default withContainer(withCurrency(MarketData));
